<template>
  <header>
    <headroom
      :up-tolerance="17"
      :down-tolerance="17"
      :offset="40"
      :z-index="1020"
      :disabled="isHeadroomDisabled"
      class="font-weight-bold mb-0 pt-3 pb-0 bg-white position-fixed general-headroom"
      data-prevent-focus-scroll
    >
      <div class="container header-link position-relative">
        <div v-if="(!isCalendarView && !isTimetableView) || !$can('view-calendars', $me)" class="header-left">
          <sdui-app-switcher
            v-if="showAppSwitcher"
            :locale="$getCurrentLocale()"
            :features.prop="appSwitcherFeatures"
            :shortcuts.prop="appSwitcherShortcuts"
            data-test="app-switcher"
            @switcher-opened="appSwitcherOpened"
            @feature-click="appSwitcherFeatureClick"
            @more-click="appSwitcherMoreClick"
          />
          <h1 class="font-weight-bold ml-2 mb-0 title-general"
              data-cy="page-header"
              v-html="$sanitize(header)"
          />

          <div v-if="isChannelsRoute" class="add-dropdown pl-2 text-decoration-none">
            <STooltip :disabled="!isChannelCreationDisabled"
                      :trigger-element-disabled="isChannelCreationDisabled"
            >
              <template #tooltip-trigger="{ triggerAttrs }">
                <!--eslint-disable-next-line sdui/require-focus-restore-on-disabled-button-->
                <b-button
                  v-bind="triggerAttrs"
                  variant="link"
                  class="align-content-center"
                  :disabled="isChannelCreationDisabled"
                  data-cy="create-channel"
                  @click="navigate('channel-creation-list')"
                >
                  <sdui-icon bg-variant="primary"
                             class="icon-add-o text-primary"
                             :circle="true"
                  />
                  <span class="sr-only">{{ $t('channels.channel_creation.create_chat') }}</span>
                </b-button>
              </template>
              <template #tooltip-text>
                <div data-cy="create-channel-disabled-tooltip">{{ $t('cloud.chat_disabled') }}</div>
              </template>
            </STooltip>
          </div>
        </div>

        <!-- timetable & calendar header -->
        <timetable-general-header-left v-else
                                       :is-calendar-view="isCalendarView"
                                       :is-timetable-view="isTimetableView"
        />

        <span class="right-links d-flex align-items-center">
          <notification-centre-bell v-if="canViewCalendar" class="fixed-font-size" />

          <b-button
            v-if="showInfoIcon"
            variant="transparent"
            class="text-muted p-0 hitbox-min-width text-right"
            @click="clyTracking.trackApp().interaction.toggleTutorial({ tutorial: currentHintName, location: 'secondary', show: true }); showInfoModal = true"
          >
            <sdui-icon class="size icon-info-o" />
            <span class="sr-only">{{ $t('global.page_info') }}</span>
          </b-button>

          <SDropDown
            id="general-dropdown"
            variant="link"
            right
            toggle-class="text-decoration-none p-0 hitbox-min-width text-right"
            data-cy="general-dropdown"
            no-caret
            @show="showMenuDropdown = true"
            @hide="showMenuDropdown = false"
          >
            <template #button-content>
              <sdui-icon class="icon-burger-o text-muted" />
              <span class="sr-only">{{ $t('global.menu') }}</span>
            </template>

            <b-dropdown-item-button
              v-if="!hasAccountSettingsInFooter"
              data-cy="settings-page"
              class="d-flex align-items-center"
              :title="$t('settings.index.icons.management_icon.description')"
              @click="navigate('settings-overview-01')"
            >
              <sdui-icon class="size text-primary" :class="$route.meta.iconSettings || 'icon-settings-o'" />
              <span class="pl-2">
                {{ $t('global.header.settings.span') }}
              </span>
            </b-dropdown-item-button>

            <li class="dropdown-divider" aria-hidden="true" />

            <b-dropdown-item-button
              v-if="showChannelPage"
              class="d-flex align-items-center"
              data-cy="general-dropdown-my-groups"
              :title="$t('global.header.groups.title')"
              @click="navigate('lite-channels')"
            >
              <sdui-icon class="size text-primary" :class="$route.meta.iconChannels || 'icon-group-o'" />
              <span class="pl-2">
                {{ $t('global.header.groups.span') }}
              </span>
            </b-dropdown-item-button>

            <li v-if="showChannelPage"
                class="dropdown-divider"
                aria-hidden="true"
            />

            <!-- for davinci-lite -->
            <b-dropdown-item-button
              v-if="showAdminPageInDropdown"
              class="d-flex align-items-center"
              :title="$t('global.header.groups.title')"
              @click="$router.push({ name: 'school-stats', params: { schoolUuid: $me.school.uuid } })"
            >
              <sdui-icon class="size text-primary" :class="$route.meta.iconChannels || 'icon-group-o'" />
              <span class="pl-2">
                {{ $t('global.header.admin.span') }}
              </span>
            </b-dropdown-item-button>

            <li v-if="showAdminPageInDropdown"
                class="dropdown-divider"
                aria-hidden="true"
            />

            <b-dropdown-item-button
              v-if="$can('manage-users', $me)"
              class="d-flex align-items-center"
              data-cy="general-dropdown-manage-users"
              :title="$t('global.header.manageUsers.title')"
              @click="navigate('users')"
            >
              <sdui-icon
                class="size text-primary"
                :class="$route.meta.iconUsers || 'icon-user-settings-o'"
                :title="$t('settings.index.icons.settings_icon.description')"
              />
              <span class="pl-2">
                {{ $t('global.header.manageUsers.span') }}
              </span>
            </b-dropdown-item-button>

            <li v-if="$can('manage-users', $me)"
                class="dropdown-divider"
                aria-hidden="true"
            />

            <b-dropdown-item-button
              v-if="showChooseAccounts"
              data-cy="switch-accounts"
              class="d-flex align-items-center"
              :title="$t('global.header.multiUser.title')"
              @click="navigate('settings-multi-users')"
            >
              <sdui-icon class="size text-primary icon-user-parent-o" :title="$t('settings.index.icons.settings_icon.description')" />
              <span class="pl-2">
                {{ $t('global.header.multiUser.span') }}
              </span>
            </b-dropdown-item-button>

            <li v-if="showChooseAccounts"
                class="dropdown-divider"
                aria-hidden="true"
            />

            <b-dropdown-item-button
              class="logout-action d-flex align-items-center"
              data-cy="logout"
              :title="$t('global.header.logout.title')"
              @click="openLogoutModal"
            >
              <sdui-icon class="size icon-logout text-danger-dark" :title="$t('settings.index.icons.settings_icon.description')" />
              <span class="pl-2 text-danger-dark">
                {{ $t('global.header.logout.span') }}
              </span>
            </b-dropdown-item-button>
          </SDropDown>
        </span>
      </div>
    </headroom>

    <SModal
      v-if="currentHintName"
      v-model="showInfoModal"
      :header-title="$t('cardinfo.sections.' + currentHintName + '.modal.title')"
      :show-footer-gradient="false"
    >
      <info-modal v-if="showInfoModal"
                  :section="currentHintName"
                  @close="showInfoModal = false"
      />
    </SModal>

    <SConfirmModal
      v-model="isLogoutModalVisible"
      :title="$t(`global.header.logout.modal.description.${$me && $me.type}`)"
      :ok-title="$t('global.header.logout.modal.submit')"
      :cancel-title="$t('global.header.logout.modal.cancel')"
      :return-focus="$route.path === '/settings' ? '#settings-logout-btn' : '#general-dropdown__BV_toggle_'"
      data-cy="deactivate-modal"
      @ok="logout"
      @hide="hideLogoutModal"
    />
  </header>
</template>

<script>
import { headroom } from 'vue-headroom'
import InfoModal from 'src/components/miscellaneous/ui/modals/InfoModal.vue'
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import multiUserMixin from '../mixins/authentification/multiUserMixin.js'
import brandingMixin from 'src/mixins/branding/brandingMixin.js'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'
import { School } from 'src/store/models/index.js'
import TimetableGeneralHeaderLeft from 'src/components/timetable/TimetableGeneralHeaderLeft.vue'
import NotificationCentreBell from '../components/notificationCentre/NotificationCentreBell.vue'
import { registerSduiAppSwitcher } from '@sdui-hq/app-switcher'
import native from 'src/config/native.js'
import STooltip from 'src/components/base/STooltip.vue'

export default {
  name: 'SduiGeneral',
  components: {
    InfoModal,
    headroom,
    TimetableGeneralHeaderLeft,
    NotificationCentreBell,
    STooltip,
  },

  mixins: [multiUserMixin, brandingMixin],
  props: {
    cardState: {
      type: Boolean,
      default: true,
    },
  },

  data: () => ({
    clyTracking,
    showInfoModal: false,
    isLogoutModalVisible: false,
    showMenuDropdown: false,
    infoCards: [
      {
        page: 'feed',
        card: 'news',
      },
      {
        page: 'timetable',
        card: 'timetable',
      },
      {
        page: 'channels',
        card: 'chat',
      },
      {
        page: 'lite-channels',
        card: 'groups',
      },
      {
        page: 'cloud-group-view-lite',
        card: 'cloud',
      },
    ],
  }),

  computed: {
    isHeadroomDisabled() {
      return this.$store.state.headerDisabled || this.showMenuDropdown
    },

    canViewCalendar() {
      return this.$can('view-calendars', this.$me)
    },

    currentHintName() {
      // This function decides to show or hide info icon in the header
      // Icon should be shown only in case if there is info card available on the page
      // And it was already closed. So in localStorage there should be following key
      // Functions also decides which info card should be shown based on route name
      const currentPageData = this.infoCards.find((s) => s.page === this.$route.name)
      return currentPageData && currentPageData.card
    },

    showInfoIcon() {
      // always show info icon, if hint cards are disabled by branding
      if (!this.brandingConfig.ui.show_hint_cards && this.currentHintName) return true

      // show info icon if hint is hidden
      return this.currentHintName && this.$me?.properties?.[`users.hints.${this.currentHintName}.hidden`]
    },

    showChannelPage() {
      return !this.$me || !['davinci-lite', 'classbook-only'].includes(this.$me?.school.properties.menu_type)
    },

    showAdminPageInDropdown() {
      if (!this.$me) return false
      return this.$can('manage-users', this.$me) && this.$me?.school.properties.menu_type === 'davinci-lite'
    },

    showChooseAccounts() {
      if (this.hasMultipleAccounts) return true

      return true
      // const betaSchools = [22231, 50761, 50196, 70556, 50731, 70701, 50746, 70882, 50160]
      // return betaSchools.includes(this.$me?.school.id) || import.meta.env.MODE	 === 'development'
    },

    hasAccountSettingsInFooter() {
      return this.$me?.school.meta.footer_menu.includes('settings')
    },

    isNoSchool() {
      return School.isNoSchool(this.$me?.school)
    },

    isCalendarView() {
      return this.$route.meta.group === 'calendar'
    },

    isTimetableView() {
      return this.$route.meta.group === 'timetable'
    },

    isChannelsRoute() {
      return this.$route.name === 'channels'
    },

    canCreateChannels() {
      return this.$can('create-channel', this.$me) || this.$can('create-conversation', this.$me)
    },

    isChannelCreationDisabled () {
      return !this.canCreateChannels || this.$wait.waiting('loading channels')
    },

    header() {
      /* use second one as fallback */
      const header = this.$route.meta.noSchoolHeader || this.$route.meta.headerText
      if (this.isNoSchool) return this.$t(header)
      return this.$t(this.$route.meta.headerText)
    },

    showAppSwitcher() {
      // Check if its demo accessibility school, which we need to disable the app switcher for now.
      if (!this.$me?.type || !this.$me?.school || this.$isA11yDemoSchool())
        return false

      const slinksForceShowAppSwitcher = [
        import.meta.env.VITE_SLINK_SDUI_GMBH_INTERNAL_SCHOOL,
        import.meta.env.VITE_SLINK_MUSTERSCHULE_KOBLENZ,
        import.meta.env.VITE_SLINK_SDUI_GMBH_SCHOOL,
      ]
      if (slinksForceShowAppSwitcher.includes(this.$me?.school?.slink)) return true
      return this.brandingConfig.appSwitcher.show_marketing && this.$me.type === 'TEACHER'
    },

    appSwitcherShortcuts() {
      const sduiGroup = this.$me?.meta?.sdui_group
      if (!sduiGroup?.shortcuts?.length) {
        return []
      }
      return sduiGroup.shortcuts
    },

    appSwitcherFeatures() {

      // LMS: Only visible to internal prod, demo prod and k8 school
      const canDisplayLMS = () => {
        return [
          import.meta.env.VITE_SLINK_SDUI_GMBH_INTERNAL_SCHOOL,
          import.meta.env.VITE_SLINK_SDUI_GMBH_SCHOOL,
          import.meta.env.VITE_SLINK_SDUI_GMBH_DEMO_SCHOOL,
        ].includes(this.$me?.school?.slink)
      }

      const defaultFeatures = {
        educa: {
          more_button: false,
          ...(!canDisplayLMS() && { lms: false }),
        },

        pupil: {
          parent_meetings: {
            active: true,
            href: 'https://www.7education.com/solutions/school-communication/parent-teacher-meetings?utm_medium=app-switcher&utm_source=7education'
          }
        },

        'additio-app': false
      }
      const sduiGroup = this.$me?.meta?.sdui_group

      // Merge feature overrides into defaults
      if (sduiGroup?.features?.length) {
        sduiGroup.features.forEach((feature) => {
          const { product, name, active, href } = feature
          // Only override features for known products — unknown products are intentionally ignored
          if (defaultFeatures[product] && name) {
            defaultFeatures[product][name] = { active, href }
          }
        })
      }

      // Teachers without an email hit a blocking error when switching to Pupil (SC-1663).
      // Show a non-blocking hint on Pupil features instead of preventing the click.
      if (!this.$me?.email) {
        Object.keys(defaultFeatures.pupil).forEach((name) => {
          if (defaultFeatures.pupil[name]) {
            defaultFeatures.pupil[name] = { ...defaultFeatures.pupil[name], emailRequired: true }
          }
        })
      }

      return defaultFeatures
    },
  },

  methods: {
    async appSwitcherFeatureClick(customEvent) {
      if (!customEvent.detail) {
        return
      }
      const { url, featureId } = customEvent.detail || {}
      if (!url) {
        return
      }
      clyTracking.trackApp().interaction.appFeatureClick({ featureId })
      await native.openUrlInAppBrowser(url)
    },

    appSwitcherMoreClick(customEvent) {
      if (!customEvent.detail) return
      const { productName } = customEvent.detail || {}
      clyTracking.trackApp().interaction.appMoreClick({ productName })
    },

    appSwitcherOpened() {
      clyTracking.trackApp().interaction.openAppSwitcher()
    },

    navigate(routeName) {
      if (this.$route.name === routeName) return
      if (routeName === 'channel-creation-list') {
        clyTracking.trackChannel('channel').start.create()
      }
      this.$router.push({ name: routeName })
    },

    openLogoutModal() {
      const { name, params, query } = this.$route

      // new middleware doeesn´t allow login key
      const routeName = name.includes('timetable') ? 'settings-overview-01' : name

      this.$router.push({ name: routeName, params: params, query: { ...query, logout: true } })
    },

    hideLogoutModal() {
      const { name, params, query } = this.$route
      this.$router.push({ name, params: params, query: { ...query, logout: undefined } })
    },

    logout: waitFor('logout', async function () {
      clyTracking.trackAuth().action.logout({ origin: 'manual' })
      if (!this.hasMultipleAccounts) {
        return this.$store.dispatch('users/logout', this.$me)
      }

      // remove current account
      await this.$store.dispatch('multiUsers/removeAndLogoutUser', { ...this.$me, authToken: this.$store.state.authToken })
      this.hideLogoutModal()
    }),
  },

  watch: {
    '$route.query.logout': {
      handler: function (newVal) {
        this.isLogoutModalVisible = !!newVal
      },

      immediate: true,
    },
  },

  created() {
    if ('customElements' in window && !customElements.get('sdui-app-switcher')) {
      registerSduiAppSwitcher()
    }
  },
}
</script>

<style scoped lang="scss">
.fixed-font-size {
  /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
  font-size: 16px;
  /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
  line-height: 20px;
}

.flex {
  display: flex;
  justify-content: space-between;
}

.header-link {
  text-decoration: none !important;
  padding-bottom: 8px;
  display: flex;
  justify-content: space-between;

  .right-links {
    .icon {
      /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
      font-size: 28px;
    }

    .btn, :deep(.btn) {
      @extend .fixed-font-size;
      display: inline-flex;
      align-items: center;
      justify-content: flex-end;
    }
  }
}

.overflow-dots h1 {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: calc(100% - 100px) !important;
}

.header-left {
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: visible;
  text-overflow: ellipsis;
  flex: 1 1 auto;
  min-width: 0;

  h1 {
    display: inline-block;
    vertical-align: middle;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
}

.add-dropdown {
  display: inline-block;
  vertical-align: middle;

  :deep(.btn) {
    padding: 0 !important;
  }

  :deep(.s-tooltip) {
    /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
    line-height: 20px;

    .btn {
      @extend .fixed-font-size;
    }
  }
}

.logout-action {
  border-top: 4px solid $color-secondary;
}

// A11y: If user has the system configured to show larger font-sizes, at some point the title is too large and breaks the layout.
.title-general {
  /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
  line-height: 36px !important;
}
</style>
